import type { DoCheck, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import type { Subscription } from 'rxjs';

import { ModifiedErrorStateMatcher } from '../../extensions/errorstatematcher/errorstatematcher';

@Component({
    selector: 'material-selectgroup',
    templateUrl: './selectgroup.component.html',
    styleUrls: ['./selectgroup.component.scss'],
    standalone: false,
})
export class MaterialSelectGroupComponent implements OnInit, DoCheck, OnChanges, OnDestroy {
    @Input() appearance: any = 'fill';
    @Input() options: any[];
    @Input() placeholder: string;
    @Input() name: string;
    @Input() title: any;
    @Input() model: any;
    @Input() item: string;
    @Input() id: string;
    @Input() classList: string[];
    @Input() required: boolean;
    @Input() errors: any[];
    @Input() warnings: any[];
    @Input() disabled: boolean;
    @Input() optionDisplayNameProperty?: string;
    @Input() optionValueProperty?: string;
    @Input() groupDisplayNameProperty?: string;
    @Input() groupValueProperty?: string;
    @Input() emitFirstEvent?: boolean;
    @Input() noSelectIfValidOptionSelected: boolean;
    @Input() hideSelectIfValueExists: boolean;
    @Input() isMultple: boolean;
    @Input() noOptionsInstruction: string;

    @Output() modelChange = new EventEmitter();
    @Output() optionData = new EventEmitter();
    @Output() debounceEventHandler = new EventEmitter();
    @Output() selectedOption = new EventEmitter();

    searchColumns: string;
    matcher = new ModifiedErrorStateMatcher();
    control = new UntypedFormControl('');
    valueSubscription: Subscription;
    private lastErrorsLength: number | undefined;

    ngDoCheck(): void {
        // Lightweight sync: if the errors array length changed (possibly mutated in place), update control state
        const currentLen = Array.isArray(this.errors) ? this.errors.length : 0;
        if (this.lastErrorsLength !== currentLen) {
            this.updateFormErrors();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        // Called before any other lifecycle hook. Use it to inject dependencies, but avoid any serious work here.
        if (changes.model) {
            if (JSON.stringify(changes.model.currentValue) !== JSON.stringify(changes.model.previousValue)) {
                this.control.setValue(changes.model.currentValue);
            }
        }
        if (changes.disabled) {
            if (changes.disabled.currentValue === true) {
                this.control.disable();
            } else {
                this.control.enable();
            }
        }
        if (changes.errors) {
            this.updateFormErrors();
        }
    }

    modelChanged = event => {
        this.model = event;
        this.modelChange.next(this.model);
        this.debounceEventHandler.emit(event);
    };

    sendOptionsData = option => {
        this.optionData.next(option);
    };

    selected = option => {
        this.selectedOption.emit(option);
    };

    ngOnInit() {
        this.appearance = this.appearance ? this.appearance : 'fill';
        this.valueSubscription = this.control.valueChanges.subscribe(event => this.modelChanged(event));
        this.updateFormErrors();
        if (this.model !== undefined && this.emitFirstEvent) {
            this.modelChanged(this.model);
        }
    }

    private updateFormErrors(): void {
        // Sync external errors input to form control state without heavy comparisons
        if (!this.errors || (Array.isArray(this.errors) && this.errors.length === 0)) {
            if (this.control.getError('errors') !== undefined) {
                this.control.setErrors(undefined);
            }
            this.lastErrorsLength = 0;
        } else {
            this.control.setErrors({ errors: this.errors });
            this.lastErrorsLength = this.errors.length;
        }
    }

    getOptionDisplayText(option: any): any {
        return this.optionDisplayNameProperty ? option[this.optionDisplayNameProperty] : option.displayName || option;
    }

    getOptionValue(option: any): any {
        return this.optionValueProperty !== undefined
            ? option[this.optionValueProperty]
            : option.value !== undefined
            ? option.value
            : option;
    }

    getTooltipTitle(): string {
        if (this.errors && this.errors.length > 0) {
            return Array.isArray(this.errors) ? this.errors.join(', ') : String(this.errors);
        }

        const targetVal = this.model !== undefined && this.model !== null && this.model !== '' ? this.model : this.title;

        if (
            this.options &&
            this.options.length > 0 &&
            targetVal !== undefined &&
            targetVal !== null &&
            targetVal !== '' &&
            targetVal !== 'undefined'
        ) {
            const groupProp = this.groupValueProperty || 'value';
            const foundDisplays: string[] = [];

            for (const group of this.options) {
                const subOptions = group?.[groupProp];
                if (Array.isArray(subOptions)) {
                    for (const subOption of subOptions) {
                        const val = this.getOptionValue(subOption);
                        const disp = this.getOptionDisplayText(subOption);

                        if (this.isMultple && Array.isArray(targetVal)) {
                            if (targetVal.includes(val) || targetVal.includes(subOption)) {
                                if (disp) {
                                    foundDisplays.push(disp);
                                }
                            }
                        } else if (val === targetVal || subOption === targetVal) {
                            return disp !== undefined && disp !== null ? String(disp) : '';
                        }
                    }
                }
            }

            if (this.isMultple && foundDisplays.length > 0) {
                return foundDisplays.join(', ');
            }
        }

        if (typeof this.title === 'string' && this.title !== 'undefined' && this.title !== '' && this.title !== this.model) {
            return this.title;
        }

        return '';
    }

    ngOnDestroy(): void {
        // Called once, before the instance is destroyed.
        this.valueSubscription && this.valueSubscription.unsubscribe();
    }
}
